import { useRef, useState, useEffect } from 'react';
import { Modal } from '../modal/Modal';
import { useIntl } from 'react-intl';

type ReasonCode = {
  value: string;
  label: string;
  requiresText?: boolean;
};

// Reusable Confirmation Dialog Component
interface ReasonPayload {
  reason: string;
  reasonDetails: string | null;
}

export enum PostModerationReasons {
  SPAM = 'spam',
  OFF_TOPIC = 'off_topic',
  PERSONAL_DATA = 'personal_data',
  SAFETY = 'safety',
  HATE_ABUSE = 'hate_abuse',
  ILLEGAL = 'illegal',
  SCAM_FRAUD = 'scam_fraud',
  NSFW = 'nsfw',
  DUPLICATE = 'duplicate',
  LOCATION_SENSITIVE = 'location_sensitive',
  OTHER = 'other',
}

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason?: ReasonPayload) => void;
  message: React.ReactNode;
  cancelButtonText?: string;
  confirmButtonText?: string;
  confirmButtonVariant?: 'default' | 'destructive' | 'warning';
  disabled?: boolean;
  isLoading?: boolean;
  showReasonCode?: boolean;
  reasonCodes?: ReasonCode[];
  reasonPlaceholder?: string;
  reasonLabel?: string;
  requireReason?: boolean;
}

export const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  message,
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Confirm',
  confirmButtonVariant = 'default',
  disabled,
  isLoading = false,
  showReasonCode = false,
  reasonCodes,
  reasonPlaceholder = 'Select a reason',
  reasonLabel = 'Reason Code',
  requireReason = false,
}: ConfirmationDialogProps) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [reasonText, setReasonText] = useState<string>('');
  const [isConfirmDisabled, setIsConfirmDisabled] = useState<boolean>(false);
  const intl = useIntl();

  const DEFAULT_REASON_CODES: ReasonCode[] = [
    {
      value: PostModerationReasons.SPAM,
      label: intl.formatMessage({
        id: 'moderation.reason.spam',
      }),
    },
    {
      value: PostModerationReasons.OFF_TOPIC,
      label: intl.formatMessage({ id: 'moderation.reason.off_topic' }),
    },
    {
      value: PostModerationReasons.PERSONAL_DATA,
      label: intl.formatMessage({ id: 'moderation.reason.personal_data' }),
    },
    {
      value: PostModerationReasons.SAFETY,
      label: intl.formatMessage({ id: 'moderation.reason.safety' }),
    },
    {
      value: PostModerationReasons.HATE_ABUSE,
      label: intl.formatMessage({ id: 'moderation.reason.hate_abuse' }),
    },
    {
      value: PostModerationReasons.ILLEGAL,
      label: intl.formatMessage({ id: 'moderation.reason.illegal' }),
    },
    {
      value: PostModerationReasons.SCAM_FRAUD,
      label: intl.formatMessage({ id: 'moderation.reason.scam_fraud' }),
    },
    {
      value: PostModerationReasons.NSFW,
      label: intl.formatMessage({ id: 'moderation.reason.nsfw' }),
    },
    {
      value: PostModerationReasons.DUPLICATE,
      label: intl.formatMessage({ id: 'moderation.reason.duplicate' }),
    },
    {
      value: PostModerationReasons.LOCATION_SENSITIVE,
      label: intl.formatMessage({ id: 'moderation.reason.location_sensitive' }),
    },
    {
      value: PostModerationReasons.OTHER,
      label: intl.formatMessage({ id: 'moderation.reason.other' }),
      requiresText: true,
    },
  ];
  const effectiveReasonCodes = reasonCodes ?? DEFAULT_REASON_CODES;

  useEffect(() => {
    let shouldDisable = Boolean(disabled);

    if (requireReason) {
      const selectedReasonObj = effectiveReasonCodes.find(
        (r) => r.value === selectedReason
      );
      const isOtherReason = selectedReasonObj?.requiresText || false;
      shouldDisable =
        shouldDisable ||
        !selectedReason ||
        (isOtherReason && !reasonText.trim());
    }

    setIsConfirmDisabled(shouldDisable);
  }, [
    selectedReason,
    reasonText,
    disabled,
    requireReason,
    reasonCodes,
    effectiveReasonCodes,
  ]);

  const handleConfirm = () => {
    if (!showReasonCode) {
      onConfirm(undefined);
      return;
    }

    const selectedReasonObj = effectiveReasonCodes.find(
      (r) => r.value === selectedReason
    );

    const reasonPayload: ReasonPayload = {
      reason:
        selectedReason === PostModerationReasons.OTHER
          ? PostModerationReasons.OTHER
          : (selectedReason as PostModerationReasons),
      reasonDetails: selectedReasonObj?.requiresText ? reasonText.trim() : null,
    };

    onConfirm(reasonPayload);
  };

  const resetForm = () => {
    setSelectedReason('');
    setReasonText('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };
  const confirmButtonClass =
    confirmButtonVariant === 'destructive'
      ? 'bg-red-600 contrast:bg-red-800 hover:bg-red-700 hover:contrast:bg-red-900 text-white contrast:text-white'
      : confirmButtonVariant === 'warning'
        ? 'bg-primary hover:bg-primary/90 text-white contrast:bg-orange-400 contrast:hover:bg-orange-500 contrast:text-black'
        : 'bg-primary hover:bg-primary/90 text-white';
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus management for accessibility
  useEffect(() => {
    if (open && dialogRef.current) {
      const timer = setTimeout(() => {
        dialogRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className="!flex items-center justify-center"
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        className="bg-gray-100 p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar rounded-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
        tabIndex={-1}
      >
        <h1
          id="confirmation-dialog-title"
          className="text-lg font-medium text-gray-900 mb-4"
        >
          Confirm Action
        </h1>
        <div
          id="confirmation-dialog-description"
          className="text-sm text-gray-700 mb-6"
        >
          {message}
        </div>
        {showReasonCode && (
          <div className="mb-4">
            <label
              htmlFor="reason-code"
              className="block text-sm font-medium text-gray-700 mb-1"
              aria-label={reasonLabel}
            >
              {reasonLabel}
            </label>
            <select
              id="reason-code"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              className="w-full bg-light-active px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="">{reasonPlaceholder}</option>
              {effectiveReasonCodes.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
            {effectiveReasonCodes.find((r) => r.value === selectedReason)
              ?.requiresText && (
              <div className="mt-2">
                <textarea
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="Please provide details"
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex justify-center rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={cancelButtonText}
            disabled={disabled}
          >
            {cancelButtonText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isConfirmDisabled || isLoading}
            className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${confirmButtonClass}`}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              confirmButtonText
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
